import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { kebabCase } from 'lodash';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { getCreativeId } from 'client/engagement-handlers/helper';
import { TooltipItem } from 'site-modules/shared/components/tooltip-item/tooltip-item';

export function DetailsField({ name, value, id, customClass, tooltipText, dtClass, ddClass }) {
  const fieldRef = useRef();

  const onTooltipOpen = useCallback(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_VIEW_CONTENT,
        subaction_name: TrackingConstant.VIEW_TOOLTIP,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
        creative_id: getCreativeId(fieldRef.current),
        value: name,
      },
    });
  }, [name]);

  const nameToShow =
    name === 'Edmunds EV Range' ? (
      <span className="dt-ev-range px-0_5 bg-turquoise-90 rounded-pill d-inline-block size-14 fw-medium">
        <i className="icon-power2" aria-hidden />
        {` ${name}`}{' '}
      </span>
    ) : (
      name
    );

  const nameId = `${kebabCase(name)}-${id}`;
  return (
    <dl ref={fieldRef} className={classnames('d-flex align-items-baseline mb-0_25', customClass)}>
      <dt className={classnames('mb-0', dtClass)} aria-labelledby={nameId}>
        {nameToShow}
        {!!tooltipText && (
          <TooltipItem
            id={`${nameId}-tooltip`}
            item={{ text: tooltipText, placement: 'right' }}
            className="label-tooltip pos-r"
            iconClassName="heading-7 text-primary"
            innerClassName="p-1 medium"
            autohide={false}
            onTooltipOpen={onTooltipOpen}
            delay={{ show: 0, hide: 750 }}
          />
        )}
      </dt>
      <dd id={nameId} className={classnames('mb-0', ddClass)}>
        {value}
      </dd>
    </dl>
  );
}

DetailsField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]).isRequired,
  id: PropTypes.string.isRequired,
  customClass: PropTypes.string,
  dtClass: PropTypes.string,
  ddClass: PropTypes.string,
  tooltipText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

DetailsField.defaultProps = {
  customClass: null,
  dtClass: 'small',
  ddClass: 'fw-bold',
  tooltipText: null,
};
